<template>
  <!--
    Issue #45–47 — NotificationCenter.vue
    Tareas 16.1–16.2: panel deslizable, store, tipos, prioridad, timestamp
    Tareas 16.3–16.6: NotificationCard, toasts, agrupación
    Tareas 16.7–16.8: preferencias por tipo, historial
  -->
  <div class="nc-wrapper">
    <!-- ── Botón del header ──────────────────────────────────────────────── -->
    <button
      ref="triggerRef"
      class="nc-bell"
      type="button"
      :aria-label="`Notificaciones${store.unreadCount ? `, ${store.unreadCount} sin leer` : ''}`"
      :aria-expanded="open"
      aria-controls="nc-panel"
      @click="toggle"
    >
      <i class="pi pi-bell" aria-hidden="true" />
      <span
        v-if="store.unreadCount"
        class="nc-badge"
        :class="{ 'nc-badge--critical': store.criticalCount > 0 }"
        aria-hidden="true"
      >
        {{ store.unreadCount > 9 ? '9+' : store.unreadCount }}
      </span>
    </button>

    <!-- ── Panel deslizable ──────────────────────────────────────────────── -->
    <Teleport to="body">
      <Transition name="nc-slide">
        <div
          v-if="open"
          id="nc-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="nc-panel-title"
          class="nc-panel"
          ref="panelRef"
          tabindex="-1"
          @keydown.esc="close"
        >
          <!-- Cabecera del panel -->
          <div class="nc-header">
            <h2 id="nc-panel-title" class="nc-title">
              <i class="pi pi-bell" aria-hidden="true" /> Notificaciones
              <span v-if="store.unreadCount" class="nc-title-count">
                ({{ store.unreadCount }} sin leer)
              </span>
            </h2>
            <div class="nc-header-actions">
              <button
                v-if="activeTab === 'list' && store.unreadCount"
                class="nc-btn-text"
                type="button"
                aria-label="Marcar todas las notificaciones como leídas"
                @click="store.markAllRead()"
              >
                <i class="pi pi-check" aria-hidden="true" /> Marcar todas
              </button>
              <button
                class="nc-close"
                type="button"
                aria-label="Cerrar panel de notificaciones"
                @click="close"
              >
                <i class="pi pi-times" aria-hidden="true" />
              </button>
            </div>
          </div>

          <!-- Pestañas (tarea 16.7 — Preferencias) -->
          <div class="nc-tabs" role="tablist" aria-label="Secciones del panel">
            <button
              role="tab"
              :aria-selected="activeTab === 'list'"
              class="nc-tab"
              :class="{ 'nc-tab--active': activeTab === 'list' }"
              @click="activeTab = 'list'"
            >
              <i class="pi pi-list" aria-hidden="true" /> Notificaciones
            </button>
            <button
              role="tab"
              :aria-selected="activeTab === 'prefs'"
              class="nc-tab"
              :class="{ 'nc-tab--active': activeTab === 'prefs' }"
              @click="activeTab = 'prefs'"
            >
              <i class="pi pi-cog" aria-hidden="true" /> Preferencias
            </button>
          </div>

          <!-- ── Tab: Lista ── -->
          <template v-if="activeTab === 'list'">
            <!-- Filtros por tipo (tarea 16.1.4) -->
            <div class="nc-filters" role="group" aria-label="Filtrar por tipo">
              <button
                v-for="f in FILTERS"
                :key="f.value"
                class="nc-filter"
                :class="{ 'nc-filter--active': store.activeFilter === f.value }"
                @click="store.setFilter(f.value as any)"
              >
                <span :class="f.dotClass" class="nc-dot" aria-hidden="true" />
                {{ f.label }}
              </button>
            </div>

            <!-- Lista de notificaciones -->
            <div
              class="nc-list"
              role="list"
              aria-label="Lista de notificaciones"
              aria-live="polite"
            >
              <template v-if="store.filtered.length">
                <NotificationCard
                  v-for="n in store.filtered"
                  :key="n.id"
                  :notification="n"
                  @mark-read="store.markRead(n.id)"
                  @dismiss="store.dismiss(n.id)"
                />
              </template>
              <div v-else class="nc-empty" role="status">
                <i class="pi pi-inbox nc-empty-icon" aria-hidden="true" />
                <p>No hay notificaciones.</p>
              </div>
            </div>

            <!-- Pie del panel (lista) -->
            <div class="nc-footer">
              <button
                v-if="store.notifications.length"
                class="nc-clear-btn"
                type="button"
                @click="store.clearAll()"
              >
                <i class="pi pi-trash" aria-hidden="true" /> Limpiar todo
              </button>
            </div>
          </template>

          <!-- ── Tab: Preferencias (tarea 16.7) ── -->
          <template v-else-if="activeTab === 'prefs'">
            <div class="nc-prefs" role="region" aria-labelledby="nc-prefs-heading">
              <h3 id="nc-prefs-heading" class="nc-prefs-heading">Tipos de notificación</h3>
              <p class="nc-prefs-sub">Elige qué notificaciones quieres recibir.</p>

              <!-- Toggles por tipo (tarea 16.7.1) -->
              <ul class="nc-pref-list">
                <li
                  v-for="t in PREF_TYPES"
                  :key="t.key"
                  class="nc-pref-item"
                >
                  <div class="nc-pref-info">
                    <span class="nc-pref-icon" :class="`icon-wrap--${t.key}`" aria-hidden="true">
                      <i :class="t.icon" />
                    </span>
                    <div class="nc-pref-text">
                      <span class="nc-pref-label">{{ t.label }}</span>
                      <span class="nc-pref-desc">{{ t.description }}</span>
                    </div>
                  </div>
                  <label class="nc-switch">
                    <input
                      v-model="store.preferences[t.key as keyof typeof store.preferences]"
                      type="checkbox"
                      role="switch"
                      :aria-checked="store.preferences[t.key as keyof typeof store.preferences]"
                      :aria-label="`Activar notificaciones de tipo ${t.label}`"
                      @change="store.savePreferences()"
                    />
                    <span class="nc-switch-track" aria-hidden="true">
                      <span class="nc-switch-thumb" />
                    </span>
                  </label>
                </li>
              </ul>

              <!-- Historial y limpieza (tarea 16.8) -->
              <div class="nc-pref-cleanup">
                <h4 class="nc-cleanup-title">Historial</h4>
                <p class="nc-cleanup-desc">
                  Se guardan las últimas 50 notificaciones automáticamente.
                </p>
                <p class="nc-cleanup-count">
                  Total almacenadas: <strong>{{ store.notifications.length }}</strong>
                </p>
                <button
                  class="nc-cleanup-btn"
                  type="button"
                  @click="store.clearAll()"
                >
                  <i class="pi pi-trash" aria-hidden="true" /> Limpiar historial
                </button>
              </div>
            </div>
          </template>
        </div>
      </Transition>

      <!-- Overlay -->
      <div
        v-if="open"
        class="nc-overlay"
        aria-hidden="true"
        @click="close"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { useNotificationsStore } from '@/stores/notifications'
import NotificationCard from './NotificationCard.vue'

const store = useNotificationsStore()
const open = ref(false)
const activeTab = ref<'list' | 'prefs'>('list')
const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)

const FILTERS = [
  { value: 'todas',       label: 'Todas',        dotClass: '' },
  { value: 'info',        label: 'Info',          dotClass: 'nc-dot--info' },
  { value: 'advertencia', label: 'Advertencias',  dotClass: 'nc-dot--warn' },
  { value: 'error',       label: 'Errores',       dotClass: 'nc-dot--error' },
  { value: 'exito',       label: 'Éxitos',        dotClass: 'nc-dot--ok' },
]

const PREF_TYPES = [
  { key: 'info',        label: 'Información',   description: 'Actualizaciones generales del sistema.',        icon: 'pi pi-info-circle' },
  { key: 'advertencia', label: 'Advertencias',  description: 'Situaciones que requieren tu atención.',         icon: 'pi pi-exclamation-triangle' },
  { key: 'error',       label: 'Errores',       description: 'Problemas críticos que necesitan resolución.',   icon: 'pi pi-times-circle' },
  { key: 'exito',       label: 'Éxitos',        description: 'Confirmaciones de operaciones completadas.',     icon: 'pi pi-check-circle' },
]

async function toggle() {
  open.value = !open.value
  if (open.value) {
    await nextTick()
    panelRef.value?.focus()
  }
}

function close() {
  open.value = false
  triggerRef.value?.focus()
}

function handleKeydown(e: KeyboardEvent) {
  if (open.value && e.key === 'Escape') close()
}

onMounted(() => document.addEventListener('keydown', handleKeydown))
onUnmounted(() => document.removeEventListener('keydown', handleKeydown))
</script>

<style scoped>
/* ── Wrapper ── */
.nc-wrapper { position: relative; display: inline-flex; }

/* ── Botón bell ── */
.nc-bell {
  position: relative;
  background: none; border: none; cursor: pointer;
  color: var(--text-color, #1e293b);
  font-size: 1.1rem; padding: 0.35rem;
  border-radius: 0.5rem; display: flex; align-items: center;
}
.nc-bell:focus { outline: 2px solid var(--primary-color, #6366f1); }

/* ── Badge ── */
.nc-badge {
  position: absolute; top: -2px; right: -2px;
  width: 18px; height: 18px; border-radius: 50%;
  background: var(--primary-color, #6366f1); color: #fff;
  font-size: 0.6rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.nc-badge--critical { background: #ef4444; }

/* ── Panel ── */
.nc-panel {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: min(360px, 100vw);
  background: var(--surface-card, #fff);
  border-left: 1px solid var(--surface-d, #e5e7eb);
  box-shadow: -4px 0 24px rgba(0,0,0,0.14);
  display: flex; flex-direction: column;
  z-index: 9900; outline: none;
}

/* ── Overlay ── */
.nc-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.25);
  z-index: 9800;
}

/* ── Cabecera ── */
.nc-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.25rem; border-bottom: 1px solid var(--surface-d, #e5e7eb);
  gap: 0.5rem; flex-wrap: wrap; flex-shrink: 0;
}
.nc-title { font-size: 1rem; font-weight: 700; color: var(--text-color, #1e293b); display: flex; align-items: center; gap: 0.4rem; }
.nc-title-count { font-size: 0.78rem; color: var(--text-color-secondary, #64748b); font-weight: 400; }
.nc-header-actions { display: flex; align-items: center; gap: 0.5rem; }
.nc-btn-text { background: none; border: none; cursor: pointer; font-size: 0.78rem; color: var(--primary-color, #6366f1); display: flex; align-items: center; gap: 0.25rem; padding: 0.25rem; }
.nc-close { background: none; border: none; cursor: pointer; color: var(--text-color-secondary, #64748b); padding: 0.25rem; border-radius: 0.25rem; }
.nc-close:focus { outline: 2px solid var(--primary-color, #6366f1); }

/* ── Pestañas ── */
.nc-tabs {
  display: flex; border-bottom: 1px solid var(--surface-d, #e5e7eb);
  flex-shrink: 0;
}
.nc-tab {
  flex: 1; padding: 0.6rem; border: none; background: none; cursor: pointer;
  font-size: 0.8rem; color: var(--text-color-secondary, #64748b);
  display: flex; align-items: center; justify-content: center; gap: 0.3rem;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
}
.nc-tab--active { color: var(--primary-color, #6366f1); border-bottom-color: var(--primary-color, #6366f1); font-weight: 600; }

/* ── Filtros ── */
.nc-filters {
  display: flex; gap: 0.25rem; flex-wrap: wrap;
  padding: 0.5rem 1rem; border-bottom: 1px solid var(--surface-d, #e5e7eb);
  flex-shrink: 0;
}
.nc-filter {
  display: flex; align-items: center; gap: 0.3rem;
  padding: 0.3rem 0.6rem; border-radius: 99px; border: 1.5px solid transparent;
  font-size: 0.75rem; font-weight: 500; cursor: pointer;
  background: var(--surface-ground, #f8fafc); color: var(--text-color-secondary, #64748b);
  transition: border-color 0.15s;
}
.nc-filter--active { border-color: var(--primary-color, #6366f1); color: var(--primary-color, #6366f1); }
.nc-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--surface-d, #cbd5e1); }
.nc-dot--info { background: #3b82f6; }
.nc-dot--warn { background: #f59e0b; }
.nc-dot--error { background: #ef4444; }
.nc-dot--ok { background: #22c55e; }

/* ── Lista ── */
.nc-list { flex: 1; overflow-y: auto; padding: 0.5rem 0; scrollbar-width: thin; }
.nc-empty { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; padding: 2rem; color: var(--text-color-secondary, #64748b); text-align: center; }
.nc-empty-icon { font-size: 2rem; opacity: 0.4; }

/* ── Footer ── */
.nc-footer { padding: 0.75rem 1.25rem; border-top: 1px solid var(--surface-d, #e5e7eb); flex-shrink: 0; }
.nc-clear-btn { background: none; border: none; cursor: pointer; font-size: 0.8rem; color: #dc2626; display: flex; align-items: center; gap: 0.3rem; }

/* ── Preferencias ── */
.nc-prefs { flex: 1; overflow-y: auto; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 1rem; }
.nc-prefs-heading { font-size: 0.9rem; font-weight: 700; color: var(--text-color, #1e293b); margin: 0; }
.nc-prefs-sub { font-size: 0.78rem; color: var(--text-color-secondary, #64748b); margin: 0; }
.nc-pref-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem; }
.nc-pref-item { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.nc-pref-info { display: flex; align-items: center; gap: 0.6rem; flex: 1; min-width: 0; }
.nc-pref-icon { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; flex-shrink: 0; }
.icon-wrap--info        { background: #dbeafe; color: #2563eb; }
.icon-wrap--advertencia { background: #fef3c7; color: #b45309; }
.icon-wrap--error       { background: #fee2e2; color: #dc2626; }
.icon-wrap--exito       { background: #dcfce7; color: #16a34a; }
.nc-pref-text { display: flex; flex-direction: column; min-width: 0; }
.nc-pref-label { font-size: 0.83rem; font-weight: 600; color: var(--text-color, #1e293b); }
.nc-pref-desc { font-size: 0.72rem; color: var(--text-color-secondary, #64748b); line-height: 1.3; }

/* ── Toggle switch ── */
.nc-switch { position: relative; flex-shrink: 0; }
.nc-switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.nc-switch-track {
  display: block; width: 36px; height: 20px; border-radius: 10px;
  background: #cbd5e1; cursor: pointer; transition: background 0.2s; position: relative;
}
.nc-switch input:checked ~ .nc-switch-track { background: var(--primary-color, #6366f1); }
.nc-switch-thumb {
  position: absolute; top: 2px; left: 2px;
  width: 16px; height: 16px; border-radius: 50%; background: #fff;
  transition: transform 0.2s;
}
.nc-switch input:checked ~ .nc-switch-track .nc-switch-thumb { transform: translateX(16px); }
.nc-switch input:focus-visible ~ .nc-switch-track { outline: 2px solid var(--primary-color, #6366f1); }

/* ── Limpieza ── */
.nc-pref-cleanup { border-top: 1px solid var(--surface-d, #e5e7eb); padding-top: 1rem; display: flex; flex-direction: column; gap: 0.4rem; }
.nc-cleanup-title { font-size: 0.85rem; font-weight: 600; color: var(--text-color, #1e293b); margin: 0; }
.nc-cleanup-desc { font-size: 0.75rem; color: var(--text-color-secondary, #64748b); margin: 0; }
.nc-cleanup-count { font-size: 0.78rem; color: var(--text-color-secondary, #64748b); margin: 0; }
.nc-cleanup-btn { background: none; border: 1px solid #fca5a5; border-radius: 0.375rem; cursor: pointer; font-size: 0.78rem; color: #dc2626; display: flex; align-items: center; gap: 0.3rem; padding: 0.4rem 0.75rem; align-self: flex-start; margin-top: 0.25rem; }
.nc-cleanup-btn:hover { background: #fee2e2; }

/* ── Transición ── */
.nc-slide-enter-active, .nc-slide-leave-active { transition: transform 0.25s ease; }
.nc-slide-enter-from, .nc-slide-leave-to { transform: translateX(100%); }
</style>
